'use client';

import { useEffect, useRef } from 'react';
import {
  isUsableUsername,
  sanitizeUsernameInput,
  slugifyUsername,
  suffixedCandidates,
  USERNAME_MIN_LENGTH,
  usernameCandidates,
} from '@peach/types/users/username.js';
import { checkUsernameAvailability } from '@/app/actions/profile';

/** Her link as she is about to claim it, and whether it can be hers. */
export interface ClaimAddressState {
  username: string;
  status: 'free' | 'taken' | 'invalid' | 'checking';
}

/** Why the claim has to wait on the link, in her words; null when it can go. */
export function addressProblem(address: ClaimAddressState | null): string | null {
  if (!address) return null;
  switch (address.status) {
    case 'free':
      return null;
    case 'checking':
      return 'Checking your link.';
    case 'taken':
      return 'That link is taken. Pick another.';
    case 'invalid':
      return address.username.length < USERNAME_MIN_LENGTH
        ? `A link needs at least ${USERNAME_MIN_LENGTH} characters.`
        : 'That link is not available. Pick another.';
  }
}

/** The link as it will be claimed: a trailing `_` survives typing, never a claim. */
export const claimedUsername = (address: ClaimAddressState) => address.username.replace(/_+$/, '');

const CHECK_DELAY_MS = 350;

/**
 * Her address, decided before she is asked for anything: the handle when it is
 * free, else her name's shapes, else the handle with a small number. Nothing
 * at all while every idea is taken — the claim then keeps the signup's own
 * username. Runs once per handle; a name edited later does not move the link.
 */
export function useAddressSuggestion(
  handle: string,
  name: string,
  onChange: (next: ClaimAddressState) => void,
  /** A link she already settled on, tried first so a reload keeps it while it is still free. */
  kept?: string | null
) {
  useEffect(() => {
    let live = true;
    const base = slugifyUsername(handle);
    const ideas = [
      kept ?? '',
      base,
      ...usernameCandidates({ name }),
      ...suffixedCandidates(base, 5),
    ];
    void (async () => {
      for (const idea of [...new Set(ideas)].filter(Boolean)) {
        const free = await checkUsernameAvailability(idea);
        if (!live) return;
        if (free.success && free.data) return onChange({ username: idea, status: 'free' });
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);
}

/**
 * Typing a link, with the same availability check the claim runs, debounced
 * under the typing. `own` is the link already theirs: the one taken name that
 * is free to them.
 */
export function useAddressTyping(
  own: string | undefined,
  onChange: (next: ClaimAddressState) => void
) {
  const latest = useRef(0);
  return (raw: string) => {
    const username = sanitizeUsernameInput(raw);
    const candidate = username.replace(/_+$/, '');
    const seq = ++latest.current;
    if (candidate === own) return onChange({ username, status: 'free' });
    if (!isUsableUsername(candidate)) return onChange({ username, status: 'invalid' });
    onChange({ username, status: 'checking' });
    window.setTimeout(async () => {
      if (seq !== latest.current) return;
      const free = await checkUsernameAvailability(candidate);
      if (seq !== latest.current) return;
      onChange({ username, status: free.success && free.data ? 'free' : 'taken' });
    }, CHECK_DELAY_MS);
  };
}
